import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useEffectiveRolePath from "../../../hooks/useEffectiveRolePath";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import { ArrowBack, Refresh, Settings, Star } from "@mui/icons-material";
import { useTimetableById, useRegenerateTimetable } from "../../../hooks/queries/useTimetable";
import { toast } from "react-toastify";

const TimetableRegenerate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { rolePath } = useEffectiveRolePath();
  const { data: timetableData, isLoading: isLoadingTimetable } = useTimetableById(id);
  const regenerateMutation = useRegenerateTimetable();

  const [regenerateOptions, setRegenerateOptions] = useState({
    keepLockedSlots: true,
    adjustForConflicts: true,
    maxIterations: 1000,
    populationSize: 50,
    mutationRate: 0.1,
    notes: "",
  });

  const handleRegenerate = async () => {
    try {
      const result = await regenerateMutation.mutateAsync({
        id,
        params: regenerateOptions,
      });

      toast.success("Timetable regeneration started successfully!");
      const regeneratedId =
        result?.data?.data?.timetableId ??
        result?.data?.data?.id ??
        id;

      if (regeneratedId) {
        navigate(`${rolePath}/timetable/view/${regeneratedId}`);
      } else {
        toast.warn("Timetable regenerated, but could not determine new timetable ID.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to regenerate timetable");
    }
  };

  if (isLoadingTimetable) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          Loading timetable data...
        </Typography>
      </Box>
    );
  }

  const timetable = timetableData;
  const isManualTimetable =
    String(timetable?.generationMode || "").toLowerCase() === "fully_manual";

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate(`${rolePath}/timetable`)}
          >
            Back to Timetables
          </Button>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              <Refresh sx={{ mr: 1, verticalAlign: "middle" }} />
              Regenerate Timetable
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a new version of timetable #{id} with improved optimization
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Current Timetable Info */}
      {timetable && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Timetable Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">{timetable.name}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Academic Year
                </Typography>
                <Typography variant="body1">{timetable.academicYear}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Semester
                </Typography>
                <Typography variant="body1">{timetable.semester}</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Fitness Score
                </Typography>
                <Typography variant="body1">
                  {isManualTimetable
                    ? "N/A"
                    : timetable.fitnessScore
                      ? `${timetable.fitnessScore.toFixed(2)}%`
                      : "N/A"}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Regeneration Options */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
            <Settings sx={{ mr: 1 }} />
            Regeneration Options
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={regenerateOptions.keepLockedSlots}
                    onChange={(e) =>
                      setRegenerateOptions({
                        ...regenerateOptions,
                        keepLockedSlots: e.target.checked,
                      })
                    }
                  />
                }
                label="Keep locked slots from current timetable"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: "block" }}>
                Preserve manually locked time slots in the regenerated version
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={regenerateOptions.adjustForConflicts}
                    onChange={(e) =>
                      setRegenerateOptions({
                        ...regenerateOptions,
                        adjustForConflicts: e.target.checked,
                      })
                    }
                  />
                }
                label="Auto-resolve conflicts"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: "block" }}>
                Automatically resolve scheduling conflicts during regeneration
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Max Iterations"
                type="number"
                value={regenerateOptions.maxIterations}
                onChange={(e) =>
                  setRegenerateOptions({
                    ...regenerateOptions,
                    maxIterations: parseInt(e.target.value),
                  })
                }
                helperText="Maximum algorithm iterations (higher = better results, slower)"
                disabled={isManualTimetable}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Population Size"
                type="number"
                value={regenerateOptions.populationSize}
                onChange={(e) =>
                  setRegenerateOptions({
                    ...regenerateOptions,
                    populationSize: parseInt(e.target.value),
                  })
                }
                helperText="Genetic algorithm population size"
                disabled={isManualTimetable}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Mutation Rate"
                type="number"
                step="0.01"
                value={regenerateOptions.mutationRate}
                onChange={(e) =>
                  setRegenerateOptions({
                    ...regenerateOptions,
                    mutationRate: parseFloat(e.target.value),
                  })
                }
                helperText="Genetic algorithm mutation rate (0.0 - 1.0)"
                disabled={isManualTimetable}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={regenerateOptions.notes}
                onChange={(e) =>
                  setRegenerateOptions({
                    ...regenerateOptions,
                    notes: e.target.value,
                  })
                }
                placeholder="Add notes about why you're regenerating this timetable..."
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Warning */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong>{" "}
          {isManualTimetable
            ? "This timetable was created in manual mode. Algorithm tuning settings are not applicable."
            : "Regeneration may take several minutes depending on the complexity of your timetable and the algorithm settings. The process runs in the background and you'll be notified when complete."}
        </Typography>
      </Alert>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          onClick={() => navigate(`${rolePath}/timetable`)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Star />}
          onClick={handleRegenerate}
          disabled={regenerateMutation.isPending}
        >
          {regenerateMutation.isPending ? "Regenerating..." : "Start Regeneration"}
        </Button>
      </Box>
    </Box>
  );
};

export default TimetableRegenerate;